import { useState } from 'react';
import {
  ArrowUturnLeftIcon,
  LockClosedIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useFoldResource } from '../hooks/useFoldResource';
import { runtimeLimitsAPI } from '../services/api';
import {
  Modal,
  PageHeader,
  PageState,
  StaleNotice,
} from './founder-dashboard/onboarding/outreach/OutreachShared';
import { focusRing, formatDateTime } from './founder-dashboard/onboarding/outreach/outreachUtils';
import {
  buildUpdateBody,
  formatCount,
  formatUsageDate,
  isPinned,
  outcomeHolds,
  pickupText,
  planChange,
  rangeMessage,
  readRequestError,
  revertLabel,
  sourceLabel,
  usageSummary,
} from './runtimeLimitsForm';
import {
  DEFAULT_RANGE_DAYS,
  USAGE_RANGES,
  allowanceTone,
  barWidthPercent,
  chartModel,
  costCellText,
  costTone,
  formatDollars,
  formatLongDate,
  freeAllowanceText,
  freeUsedText,
  isGooglePriced,
  rangeCalls,
  rangeFor,
} from './runtimeLimitsUsage';

const REASON_MAX_LENGTH = 500;

const SOURCE_BADGE_CLASSES = {
  fold: 'border-blue-200 bg-blue-50 text-blue-800',
  railway_variable: 'border-purple-200 bg-purple-50 text-purple-800',
  default: 'border-gray-200 bg-gray-100 text-gray-700',
};

function describeValue(limit) {
  return `${formatCount(limit.value)} (${sourceLabel(limit.source)})`;
}

function describeChanger(limit) {
  const who = limit.updatedBy?.name || limit.updatedBy?.email;
  const when = limit.updatedAt ? formatDateTime(limit.updatedAt) : null;
  if (who && when) return `Set in The Fold by ${who} on ${when}.`;
  if (when) return `Set in The Fold on ${when}.`;
  return 'Set in The Fold.';
}

const TONE_CLASSES = {
  within_free: 'border-gray-200 bg-white',
  over_free: 'border-amber-300 bg-amber-50',
  high_cost: 'border-red-300 bg-red-50',
};

const BAR_CLASSES = {
  within_free: 'bg-blue-600',
  over_free: 'bg-amber-500',
  high_cost: 'bg-red-600',
};

const AXIS_TICK = { fontSize: 11, fill: '#6b7280' };
const TOOLTIP_STYLE = { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' };

const requestLimits = () => runtimeLimitsAPI.list().then((response) => response.data);
const requestUsage = () => runtimeLimitsAPI.usage().then((response) => response.data);


export default function RuntimeLimitsPage() {
  const { isOwner } = useAuth();
  if (!isOwner) return <OwnerRequired />;
  return <RuntimeLimitsBoard />;
}

function OwnerRequired() {
  return (
    <div className="space-y-5">
      <PageHeader title="Limits" />
      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <LockClosedIcon className="mt-0.5 h-6 w-6 flex-none" />
        <p>Fold owner access is required to view and change limits.</p>
      </div>
    </div>
  );
}

function RuntimeLimitsBoard() {
  const [list, load] = useFoldResource(requestLimits, 'Limits could not be loaded.');
  const [usage, loadUsage] = useFoldResource(requestUsage, 'Usage could not be loaded.');
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState(null);

  if (list.status === 'denied' || usage.status === 'denied') return <OwnerRequired />;

  const payload = list.status === 'ready' ? list.payload : null;
  const limits = payload?.limits ?? [];

  function openEditor(limit) {
    setNotice(null);
    setEditor({
      base: limit,
      target: 'value',
      text: String(limit.value),
      reason: '',
      acknowledged: false,
      status: { phase: 'editing', message: null },
    });
  }

  function editDraft(changes) {
    setEditor((current) => ({
      ...current,
      ...changes,
      status: current.status.phase === 'conflict' ? current.status : { phase: 'editing', message: null },
    }));
  }

  function rebaseOnCurrent() {
    setEditor((current) => ({
      ...current,
      base: current.status.current,
      target: 'value',
      text: String(current.status.current.value),
      acknowledged: false,
      status: { phase: 'editing', message: null },
    }));
  }

  function closeWith(nextNotice) {
    setEditor(null);
    setNotice(nextNotice);
  }

  function alreadySaved(current) {
    return {
      tone: 'success',
      text: `${current.label} is already ${describeValue(current)}. No retry is needed.`,
    };
  }

  async function save() {
    const plan = planChange(editor.base, editor);
    if (!plan.ok) return;
    const { base } = editor;
    const body = buildUpdateBody(editor, plan);
    setEditor((current) => ({ ...current, status: { phase: 'saving' } }));

    try {
      const response = await runtimeLimitsAPI.update(base.key, body);
      const saved = response.data.limit;
      closeWith({
        tone: 'success',
        text: response.data.unchanged
          ? `${saved.label} was already ${describeValue(saved)}. Nothing changed.`
          : `${saved.label} changed from ${describeValue(base)} to ${describeValue(saved)}.`,
      });
      load();
    } catch (error) {
      const failure = readRequestError(error, 'The limit was not changed.');

      if (failure.type === 'conflict') {
        if (outcomeHolds(failure.current, body.value)) {
          closeWith(alreadySaved(failure.current));
        } else {
          setEditor((current) => ({ ...current, status: { phase: 'conflict', current: failure.current } }));
        }
        load();
        return;
      }

      if (failure.type === 'denied') {
        closeWith({ tone: 'error', text: failure.message });
        load();
        return;
      }

      if (failure.type === 'no_response') {
        setEditor((current) => ({ ...current, status: { phase: 'checking' } }));
        const fresh = await load();
        const current = fresh?.limits?.find((limit) => limit.key === base.key);
        if (current && outcomeHolds(current, body.value)) {
          closeWith(alreadySaved(current));
          return;
        }
        setEditor((draft) => ({
          ...draft,
          status: {
            phase: 'editing',
            message: current
              ? 'The connection dropped before the server answered. The latest list shows this change did not save. You can try again.'
              : 'The connection dropped before the server answered, and the list could not be re-read. Check your connection, then try again.',
          },
        }));
        return;
      }

      setEditor((current) => ({ ...current, status: { phase: 'editing', message: failure.message } }));
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Limits"
        description="How many paid Google and AI calls Flock can make each day. A value you save here applies everywhere in Flock right away, with no redeploy."
        refreshing={Boolean(list.refreshing || usage.refreshing)}
        onRefresh={() => {
          load();
          loadUsage();
        }}
      />
      {list.status === 'ready' ? <StaleNotice loadedAt={list.loadedAt} /> : null}

      {notice ? (
        <div
          role={notice.tone === 'success' ? 'status' : 'alert'}
          className={`rounded-lg border px-4 py-3 text-sm ${
            notice.tone === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {notice.text}
        </div>
      ) : null}
      {list.status === 'ready' && list.refreshError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          The list could not be refreshed: {list.refreshError} Showing the last loaded values.
        </div>
      ) : null}

      <UsageSection usage={usage} onRetry={loadUsage} />

      {list.status === 'failed' ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
          <p className="font-semibold">Limits could not be loaded</p>
          <p className="mt-1 text-sm">{list.message}</p>
          <button
            type="button"
            onClick={load}
            className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
          >
            Try again
          </button>
        </div>
      ) : (
        <PageState
          loading={list.status === 'loading'}
          empty={limits.length === 0}
          emptyTitle="No limits reported"
          emptyMessage="The server returned no limits."
        >
          <section
            aria-label="How limits work"
            className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950"
          >
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Usage date {formatUsageDate(payload?.usageDate)} ({payload?.timeZone}). Daily budgets
                reset at midnight Central time.
              </li>
              <li>Today&apos;s usage includes calls Flock blocked after the limit was reached.</li>
              <li>Setting a daily budget to 0 pauses that service until you raise it.</li>
            </ul>
          </section>

          <div className="space-y-4">
            {limits.map((limit) => (
              <LimitCard key={limit.key} limit={limit} onChange={() => openEditor(limit)} />
            ))}
          </div>
        </PageState>
      )}

      {editor ? (
        <LimitEditor
          editor={editor}
          onEdit={editDraft}
          onRebase={rebaseOnCurrent}
          onSave={save}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </div>
  );
}

function UsageSection({ usage, onRetry }) {
  const [rangeDays, setRangeDays] = useState(DEFAULT_RANGE_DAYS);

  if (usage.status === 'loading') {
    return (
      <section aria-label="Usage and estimated cost">
        <PageState loading />
      </section>
    );
  }
  if (usage.status === 'failed') {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
        <p className="font-semibold">Usage and cost could not be loaded</p>
        <p className="mt-1 text-sm">{usage.message}</p>
        <button
          type="button"
          onClick={onRetry}
          className={`mt-4 min-h-[44px] rounded-lg border border-current px-4 py-2 text-sm font-semibold ${focusRing}`}
        >
          Try again
        </button>
      </div>
    );
  }
  if (usage.status !== 'ready') return null;

  const report = usage.payload;

  return (
    <section
      aria-labelledby="usage-title"
      className="space-y-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 id="usage-title" className="font-semibold text-gray-900">
            Usage and estimated cost
          </h4>
          <p className="mt-1 text-sm text-gray-600">
            Paid Google and AI calls through {formatUsageDate(report.usageDate)} (Central time), and what
            Google would charge for this month.
          </p>
        </div>
        <RangeToggle value={rangeDays} onChange={setRangeDays} />
      </div>

      {usage.refreshError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Usage could not be refreshed: {usage.refreshError} Showing the last loaded numbers.
        </div>
      ) : null}

      <UsageChart report={report} rangeDays={rangeDays} />
      <CostCards report={report} />
      <UsageTable report={report} />

      <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
        <p className="font-semibold text-gray-900">About these numbers</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {report.caveats.map((caveat) => (
            <li key={caveat}>{caveat}</li>
          ))}
        </ul>
        <p className="mt-3">
          Google prices as of {formatLongDate(report.pricingAsOf)}.{' '}
          <a
            href={report.pricingSourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-semibold text-blue-700 underline ${focusRing}`}
          >
            See Google&apos;s price list
          </a>
          .
        </p>
      </div>
    </section>
  );
}

function RangeToggle({ value, onChange }) {
  return (
    <div role="group" aria-label="Chart range" className="inline-flex self-start rounded-lg border border-gray-300 bg-white p-1">
      {USAGE_RANGES.map((range) => {
        const selected = range.days === value;
        return (
          <button
            key={range.days}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(range.days)}
            className={`min-h-[44px] rounded-md px-3 text-sm font-semibold ${
              selected ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            } ${focusRing}`}
          >
            {range.label}
          </button>
        );
      })}
    </div>
  );
}

function UsageChart({ report, rangeDays }) {
  const model = chartModel(report, rangeDays);
  const total = rangeCalls(report, rangeDays);

  return (
    <figure>
      <figcaption className="text-sm text-gray-700">
        <span className="text-lg font-bold text-gray-900">{formatCount(total)}</span> calls{' '}
        {rangeFor(rangeDays).summary}
      </figcaption>
      <div className="mt-3 h-72">
        <ResponsiveContainer width="100%" height="100%">
          {model.layout === 'by_limit' ? (
            <BarChart data={model.rows} layout="vertical" margin={{ top: 5, right: 24, left: 8, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
              <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} />
              <YAxis type="category" dataKey="label" width={210} tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => [formatCount(value), 'Calls today']} />
              <Bar dataKey="calls" name="Calls today" isAnimationActive={false}>
                {model.rows.map((row) => (
                  <Cell key={row.key} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <BarChart data={model.rows} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="label" interval="preserveStartEnd" minTickGap={12} tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value, name) => [formatCount(value), name]} />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              {model.series.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={`counts.${series.key}`}
                  name={series.label}
                  stackId="calls"
                  fill={series.color}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </figure>
  );
}

function CostCards({ report }) {
  const { totals } = report;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      <MoneyCard
        title="Estimated cost this month"
        dollars={totals.estimatedCostMonthToDate}
        detail="What Google would charge so far this month. AI calls are billed separately."
      />
      <MoneyCard
        title="Projected this month"
        dollars={totals.projectedCostMonth}
        detail="If the rest of the month keeps this month's pace."
      />
      {report.byKey.filter(isGooglePriced).map((line) => (
        <AllowanceCard key={line.key} line={line} />
      ))}
    </div>
  );
}

function MoneyCard({ title, dollars, detail }) {
  return (
    <div className={`rounded-lg border p-4 ${TONE_CLASSES[costTone(dollars)]}`}>
      <p className="text-sm font-semibold text-gray-700">{title}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{formatDollars(dollars)}</p>
      <p className="mt-1 text-xs text-gray-600">{detail}</p>
    </div>
  );
}

function AllowanceCard({ line }) {
  const tone = allowanceTone(line);
  const width = barWidthPercent(line.freeUsedPercent);
  return (
    <div className={`rounded-lg border p-4 ${TONE_CLASSES[tone]}`}>
      <p className="text-sm font-semibold text-gray-900">{line.label}</p>
      <p className="mt-1 text-xs text-gray-600">Free allowance used this month</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{formatCount(line.freeUsedPercent)}%</p>
      <div
        role="progressbar"
        aria-label={`${line.label}: free allowance used`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
        aria-valuetext={`${formatCount(line.freeUsedPercent)}%`}
        className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200"
      >
        <div className={`h-full rounded-full ${BAR_CLASSES[tone]}`} style={{ width: `${width}%` }} />
      </div>
      <p className="mt-2 text-xs text-gray-700">{freeUsedText(line)}</p>
      {tone === 'within_free' ? null : (
        <p className={`mt-1 text-xs font-semibold ${tone === 'high_cost' ? 'text-red-800' : 'text-amber-900'}`}>
          Projected cost this month: {formatDollars(line.projectedCostMonth)}
        </p>
      )}
      <p className="mt-2 text-xs text-gray-600">
        Google&apos;s price list: {line.sku}. {freeAllowanceText(line)}.
      </p>
    </div>
  );
}

function UsageTable({ report }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <caption className="sr-only">Calls and estimated cost for each limit</caption>
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th scope="col" className="px-4 py-3">Limit</th>
            {USAGE_RANGES.map((range) => (
              <th key={range.days} scope="col" className="px-4 py-3 text-right">
                {range.column}
              </th>
            ))}
            <th scope="col" className="px-4 py-3 text-right">Estimated cost this month</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {report.byKey.map((line) => (
            <tr key={line.key}>
              <th scope="row" className="px-4 py-3 text-left font-medium text-gray-900">
                {line.label}
                <span className="mt-0.5 block text-xs font-normal text-gray-500">{line.estimateNote}</span>
              </th>
              {USAGE_RANGES.map((range) => (
                <td key={range.days} className="px-4 py-3 text-right tabular-nums text-gray-900">
                  {formatCount(line[range.field])}
                </td>
              ))}
              <td className="px-4 py-3 text-right tabular-nums text-gray-900">{costCellText(line)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50">
          <tr>
            <th scope="row" colSpan={USAGE_RANGES.length + 1} className="px-4 py-3 text-left font-semibold text-gray-900">
              Total Google estimate this month
            </th>
            <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900">
              {formatDollars(report.totals.estimatedCostMonthToDate)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function SourceBadge({ source }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
        SOURCE_BADGE_CLASSES[source] ?? SOURCE_BADGE_CLASSES.default
      }`}
    >
      {sourceLabel(source)}
    </span>
  );
}

function Fact({ label, children }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

function LimitCard({ limit, onChange }) {
  const titleId = `limit-${limit.key}-title`;
  const usage = usageSummary(limit);
  const blocked = usage.state === 'paused' || usage.state === 'reached';
  const pickup = pickupText(limit.pickup);

  return (
    <section
      aria-labelledby={titleId}
      className={`rounded-xl border p-5 shadow-sm ${
        blocked ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 id={titleId} className="font-semibold text-gray-900">
              {limit.label}
            </h4>
            <SourceBadge source={limit.source} />
          </div>
          <p className="mt-1 text-sm text-gray-600">{limit.description}</p>
        </div>
        <button
          type="button"
          onClick={onChange}
          className={`inline-flex min-h-[44px] flex-none items-center gap-2 self-start rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 ${focusRing}`}
        >
          <PencilSquareIcon className="h-4 w-4" />
          Change<span className="sr-only"> {limit.label}</span>
        </button>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Fact label="Current value">
          <span className="text-lg font-bold">{formatCount(limit.value)}</span>{' '}
          <span className="text-gray-600">{limit.unit}</span>
        </Fact>
        <Fact label="Today's usage">{usage.text}</Fact>
        <Fact label="Used by">{limit.consumers}</Fact>
        <Fact label="When changes apply">{pickup ?? '—'}</Fact>
      </dl>

      {usage.state === 'paused' ? (
        <p className="mt-3 text-sm font-medium text-amber-900">Paused. No calls are allowed until you raise it.</p>
      ) : usage.state === 'reached' ? (
        <p className="mt-3 text-sm font-medium text-amber-900">
          Today&apos;s budget is used up. Further calls wait until midnight CT.
        </p>
      ) : null}

      {isPinned(limit) ? (
        <p className="mt-3 text-xs text-gray-600">
          {describeChanger(limit)} Without it, this limit is {formatCount(limit.fallbackValue)} (
          {sourceLabel(limit.fallbackSource)}).
        </p>
      ) : null}
    </section>
  );
}

function acknowledgementText(base, plan) {
  const next = `${formatCount(plan.next)} ${base.unit}`;
  if (plan.change === 'revert') {
    return 'Yes, stop using this Fold value and go back to the Railway setting.';
  }
  if (plan.change === 'pin') {
    const current = base.source === 'default' ? 'the default' : 'the Railway setting';
    return `Yes, use ${next} everywhere in Flock instead of ${current} (${formatCount(base.value)}).`;
  }
  return `Yes, raise this from ${formatCount(base.value)} to ${next}.`;
}

function LimitEditor({ editor, onEdit, onRebase, onSave, onClose }) {
  const { base, status } = editor;
  const plan = planChange(base, editor);
  const busy = status.phase === 'saving' || status.phase === 'checking';
  const reverting = editor.target === 'revert';
  const showInvalid = !reverting && editor.text !== '' && !plan.ok;
  const pickup = pickupText(base.pickup);

  return (
    <Modal
      title={`Change ${base.label}`}
      description={`Used by: ${base.consumers}.${pickup ? ` ${pickup}.` : ''}`}
      confirmLabel={reverting ? 'Revert' : 'Save'}
      confirmDisabled={
        !plan.ok ||
        plan.change === 'unchanged' ||
        (plan.needsAcknowledgement && !editor.acknowledged) ||
        status.phase === 'conflict'
      }
      busy={busy}
      onConfirm={onSave}
      onClose={onClose}
    >
      {status.phase === 'conflict' ? (
        <div role="alert" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <p className="font-semibold">This limit changed while you were editing. Your draft was not saved.</p>
          <p className="mt-1">
            Current saved value: {describeValue(status.current)}.{' '}
            {isPinned(status.current) ? describeChanger(status.current) : null}
          </p>
          <button
            type="button"
            onClick={onRebase}
            className={`mt-3 min-h-[44px] rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 ${focusRing}`}
          >
            Use current value
          </button>
        </div>
      ) : null}
      {status.phase === 'checking' ? (
        <div role="status" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          The connection dropped before the server answered. Checking whether the change saved…
        </div>
      ) : null}
      {status.phase === 'editing' && status.message ? (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {status.message}
        </div>
      ) : null}

      {reverting ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
          <p>
            Saving goes back to the Railway setting:{' '}
            {formatCount(base.fallbackValue)} {base.unit}.
          </p>
          <button
            type="button"
            onClick={() => onEdit({ target: 'value', acknowledged: false })}
            disabled={busy}
            className={`mt-3 min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 ${focusRing}`}
          >
            Set a value instead
          </button>
        </div>
      ) : (
        <>
          <label htmlFor="runtime-limit-value" className="block text-sm font-semibold text-gray-800">
            New value ({base.unit})
          </label>
          <input
            id="runtime-limit-value"
            type="number"
            min={base.foldMin}
            max={base.foldMax}
            step="1"
            inputMode="numeric"
            value={editor.text}
            disabled={busy}
            aria-invalid={showInvalid}
            aria-describedby={`runtime-limit-value-help${showInvalid ? ' runtime-limit-value-error' : ''}`}
            onChange={(event) => onEdit({ text: event.target.value, acknowledged: false })}
            className={`mt-2 min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 ${focusRing}`}
          />
          <p id="runtime-limit-value-help" className="mt-2 text-xs text-gray-600">
            {rangeMessage(base)} Current value: {describeValue(base)}.
          </p>
          {showInvalid ? (
            <p id="runtime-limit-value-error" aria-live="polite" className="mt-2 text-sm font-medium text-red-700">
              {plan.message}
            </p>
          ) : null}
          {isPinned(base) ? (
            <button
              type="button"
              onClick={() => onEdit({ target: 'revert', acknowledged: false })}
              disabled={busy}
              className={`mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 ${focusRing}`}
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
              {revertLabel(base)}
            </button>
          ) : null}
        </>
      )}

      {plan.ok && plan.change === 'unchanged' ? (
        <p className="mt-3 text-sm text-gray-600">This is already the saved value.</p>
      ) : null}
      {plan.ok && plan.warning ? (
        <p aria-live="polite" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          {plan.warning}
        </p>
      ) : null}
      {plan.ok && plan.needsAcknowledgement ? (
        <label className="mt-4 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          <input
            type="checkbox"
            required
            checked={editor.acknowledged}
            disabled={busy}
            onChange={(event) => onEdit({ acknowledged: event.target.checked })}
            className={`mt-0.5 h-5 w-5 flex-none ${focusRing}`}
          />
          <span>{acknowledgementText(base, plan)}</span>
        </label>
      ) : null}

      <label htmlFor="runtime-limit-reason" className="mt-4 block text-sm font-semibold text-gray-800">
        Reason (optional)
      </label>
      <textarea
        id="runtime-limit-reason"
        rows={2}
        maxLength={REASON_MAX_LENGTH}
        value={editor.reason}
        disabled={busy}
        aria-describedby="runtime-limit-reason-count"
        onChange={(event) => onEdit({ reason: event.target.value })}
        className={`mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 ${focusRing}`}
      />
      <p id="runtime-limit-reason-count" className="mt-1 text-xs text-gray-500">
        {editor.reason.length}/{REASON_MAX_LENGTH} characters. Saved with the audit record.
      </p>
    </Modal>
  );
}
